import type { MapConfiguration } from '@/common/overmind/namespaces/arcgis/config/config.types';
import type { CurrentTenantId } from '@/common/overmind/namespaces/tenants';

import type { Context } from '..';
import type { GUID } from '../effects/auth';
import { isValidGuid } from '../effects/auth';
import type { Params, RoutingContext } from '../effects/router';
import type { AuthMachine } from '../machines';
import { isInProgress } from '../machines';
import { routes } from '../machines/router';
import type { Route } from '../machines/router';
import * as internalActions from './internalActions';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { geojsonToArcGIS } from "@terraformer/arcgis";
import Graphic from "@arcgis/core/Graphic";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import { FeatureWithIDs } from '../effects/adapters/baseadapter';
import { uniqBy } from 'ramda';
import Extent from '@arcgis/core/geometry/Extent';
import type Geometry from '@arcgis/core/geometry/Geometry';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import { geoJSON, latLng, LatLngBounds } from 'leaflet';

export {
  onInitializeOvermind,
  setApplicationConfig,
  setGridCalcApiUrls,
  setAuthAsInitialized,
  authInitialized,
  loggedOut,
  arcGisTokenReady
} from './onInitializeOvermind';

export { authorize } from './onInitializeOvermind/authorize';

export const internal = internalActions;

export type OpenPagePayload = {
  page: Route;
  params?: Params;
  query?: Params;
};

export const pageOpened = (
  { effects }: Context,
  { page, params, query }: OpenPagePayload
) => {
  effects.router.goTo({ url: routes[page], params, query });
};

export const showUnauthorized = (context: Context) => {
  context.state.auth.send('STOP_AUTHENTICATING');
  pageOpened(context, { page: 'unauthorized' });
};

export const showCalculationTacticResult = (context: Context) => {
  if(context.state.calculation.calculationContext.type === "AVAILABLE") {
    context.actions.pageOpened({
      page: "executeCalculationSetUsing",
      query: context.state.calculation.calculationContext.calculationContext
    })
  }
  // pageOpened(context, { page: 'calculationv2' });
};

export const showHome = (context: Context, payload?: { tenantId?: GUID }) => {
  const isCompletingArcGISAuthentication =
    context.effects.locationPathnameProvider.pathname().includes('arcgis');
  if (!isCompletingArcGISAuthentication) {
    pageOpened(context, {
      page: 'rootWithTenantId',
      params: payload?.tenantId ? { tenantId: payload.tenantId } : {}
    });
  }
};

export const showHomeWithLocation = async (context: Context) => {
  if(context.state.config.type === "AVAILABLE") {
    const applicationId = context.state.config.config.auth.applicationId;
    const tenantId = await context.actions.tenants.getTenantForUserSession({
      applicationId: applicationId as unknown as GUID
    });

    const features = context.state.arcgis.featureGeometries.value?.flatMap(fl => fl.features) || [];

    if(context.state.calculation.resultsBrowsingMode.current === "BROWSING_GRID_ELEMENT") {
      const gridElementObjectId = context.state.calculation.resultsBrowsingMode.gridElementObjectId;
      const featureToSelect = features.filter(f => f.gc.globalId === gridElementObjectId);
      const uniqueFeatures = uniqBy((f: FeatureWithIDs<unknown>) => f.gc.globalId, featureToSelect);
      if(uniqueFeatures.length === 1) {
        context.actions.selectedFeature.setSelectedFeature(uniqueFeatures[0]);
      }
    } else {
      context.actions.selectedFeature.unselectFeature()
    }
    
    const rawBounds = context.state.map.bounds as LatLngBounds & { _southWest?: { lat: number; lng: number }; _northEast?: { lat: number; lng: number } } | undefined;
    const mapBounds = rawBounds?._southWest && rawBounds?._northEast
      ? new LatLngBounds(
          latLng(rawBounds._southWest.lat, rawBounds._southWest.lng),
          latLng(rawBounds._northEast.lat, rawBounds._northEast.lng)
        )
      : undefined;
    const featuresInBounds = mapBounds ? features.filter(f => {
      const geojsonLayer = geoJSON(f)
      const featureBounds = geojsonLayer.getBounds();
      return featureBounds.isValid() && mapBounds.contains(featureBounds);
    }) : [];
    
    await context.actions.navigateTo({ features: featuresInBounds });
    context.actions.calculation.unloadCalculation();
    context.actions.showHome({ tenantId });
    context.actions.internal.setRoute({ route: 'rootWithTenantId', params: { tenantId } });
  }
}

export const showHomeWithRelatedGridElement = async (context: Context) => {
  if(context.state.config.type === "AVAILABLE") {
    const applicationId = context.state.config.config.auth.applicationId;
    const tenantId = await context.actions.tenants.getTenantForUserSession({
      applicationId: applicationId as unknown as GUID
    });
    if(
      context.state.calculation.externallyTriggeredCalculationSetExecution.current === "READY"
      && context.state.arcgis.config.value?.mapSource.layerIds
    )
    if(context.effects.arcgisMapAdapter.getAdapter()) {
      const featuresInLayers = await context.effects.arcgisMapAdapter.getAdapter()?.getFeatures(
        [context.state.calculation.externallyTriggeredCalculationSetExecution.calculationContext.relatedObjectId],
        context.state.arcgis.config.value.mapSource.layerIds,
        context.state.calculation.gridVersionName
      );
      const features = featuresInLayers?.flatMap(fl => fl.features) || [];
      context.actions.selectedFeature.setSelectedFeature(features[0]);
      await context.actions.navigateTo({ features });
      // context.state.map.view?.highlight(features[0])
    }
    context.actions.calculation.unloadCalculation();
    context.actions.showHome({ tenantId });
    context.actions.internal.setRoute({ route: 'rootWithTenantId', params: { tenantId } });
  }
}
export const handleHomeRouteWithTenantId = async (
  context: Context,
  params: RoutingContext['params']
) => {
  const tenantId = params.tenantId;
  if (isValidGuid(tenantId)) {
    context.state.tenants.tenantFromRoute = {
      type: 'ROUTED',
      data: { tenantId }
    };
    context.actions.tenants.selectTenantById(tenantId);
  }
};

export const handleArcGisSignInRoute = async (context: Context) => {
  /**
   * This handler is required so that application router can match this path.
   * [esri.IdentityManager](https://developers.arcgis.com/javascript/latest/api-reference/esri-identity-IdentityManager.html)
   * handles HTTP redirect callback implicitly but it's not immediately available. We have to wait for it as it makes more calls to the Esri API after consuming it from URL.
   */
  if (context.state.auth.current === 'AWAITING_ARCGIS_AUTHENTICATION') return;
  context.state.auth.send('ARCGIS_AUTHENTICATING_FROM_REDIRECTION');
  const result = await context.effects.arcgis.auth.identity.getAccessToken();
  const arcgisToken = result.arcgisToken;
  if (arcgisToken) {
    await context.effects.arcgisMapAdapter.getAdapter()?.setToken(arcgisToken);
    const calculationContext =
      context.effects.storage.get('calculationContext');
    context.state.auth.send('ARCGIS_AUTHENTICATION_COMPLETED', arcgisToken);
    if (calculationContext) {
      context.effects.storage.set('calculationContext', undefined);
      const { arcgisToken: _, ...queryWithoutToken } = calculationContext;
      context.effects.router.goTo({
        url: routes.executeCalculationSetUsing,
        query: queryWithoutToken
      });
    } else {
      if (context.state.config.type === 'AVAILABLE') {
        const applicationId = context.state.config.config.auth.applicationId;
        const tenantId = await context.actions.tenants.getTenantForUserSession({
          applicationId: applicationId as unknown as GUID
        });
        context.effects.router.goTo({
          url: routes.rootWithTenantId,
          params: { tenantId }
        });
      } else {
        context.effects.router.goTo({ url: routes.root });
      }
    }
  }
};

export const handleArcGisToken = async (
  context: Context,
  {
    currentTenant,
    auth,
    config,
    arcgisConfig,
    featureToggles
  }: {
    currentTenant: CurrentTenantId;
    auth: AuthMachine;
    config: string;
    arcgisConfig: MapConfiguration | undefined;
    featureToggles: Context['state']['featureToggles'];
  }
) => {
  context.effects.log.info(
    'Reaction: fetching arcgis token',
    currentTenant,
    auth.current,
    config,
    arcgisConfig,
    featureToggles
  );
  if (!(isInProgress(auth) && config === 'AVAILABLE' && arcgisConfig)) return;
  if (!featureToggles?.isNewAuthVersionEnabled) {
    context.actions.arcgis.token.getArcGISToken();
  } else {
    await context.effects.arcgis.auth.identity.registerTenants(arcgisConfig);
  }
};


type SupportedGeoJSONFeatureGeometryTypes = (GeoJSON.Point | GeoJSON.MultiPoint | GeoJSON.Polygon | GeoJSON.LineString | GeoJSON.MultiLineString)["type"];
export const mapGeojsonToArcGisGeometry = (feature: FeatureWithIDs<unknown>): Point | Multipoint | Polyline | Polygon | undefined => {

  const map: Record<SupportedGeoJSONFeatureGeometryTypes, () => Point | Multipoint | Polyline | Polygon> = {
    Point: () => {
      const graphic = geojsonToArcGIS(feature) as Graphic;
      const point = graphic.geometry as Point
      return new Point(point)
    },
    MultiPoint: () => {
      const graphic = geojsonToArcGIS(feature) as Graphic;
      const multiPoint = graphic.geometry as Multipoint
      return new Multipoint(multiPoint)
    },
    Polygon: () => {
      const graphic = geojsonToArcGIS(feature) as Graphic;
      const polygon = graphic.geometry as Polygon
      return new Polygon(polygon)
    },
    LineString: () => {
      const graphic = geojsonToArcGIS(feature) as Graphic;
      const polyline = graphic.geometry as Polyline
      return new Polyline(polyline)
    },
    MultiLineString: () => {
      const graphic = geojsonToArcGIS(feature) as Graphic;
      const polyline = graphic.geometry as Polyline
      return new Polyline(polyline)
    }
  }

  const geometryType = feature.geometry.type as SupportedGeoJSONFeatureGeometryTypes;
  if(map[geometryType]) {
    return map[geometryType]()
  }

  return undefined
}
export async function navigateTo(context: Context, payload: { features: FeatureWithIDs<unknown>[] }) {
  const uniqueFeatures = uniqBy((f: FeatureWithIDs<unknown>) => f.gc.globalId, payload.features);
  if(uniqueFeatures.length === 0) return;
  if(uniqueFeatures.length === 1) {
    const feature = uniqueFeatures[0];
    const geometry = mapGeojsonToArcGisGeometry(feature)
    if(!geometry) return;
    context.state.map.initialExtent = geometry.toJSON();
    return;
  }
  if (uniqueFeatures.length > 1) {
    const featureCollection = {
      type: "FeatureCollection",
      features: uniqueFeatures
    };
    const blob = new Blob([JSON.stringify(featureCollection)], {
      type: "application/json"
    });

    const url = URL.createObjectURL(blob);
    const layer = new GeoJSONLayer({
      url
    });
    const extent = await layer.queryExtent();
    context.state.map.initialExtent = extent.extent.toJSON();
  }
}

